import { UnknownAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { CarInformation, InitialData, StepData, StepDataData } from '../Types/FitAssistantData.ts';
import { FitAssistantProductsResponse, GetProductsQuery } from '../Types/FitAssistantGraphQLTypes.ts';
import {
  SetFitStatusForAllProducts,
  SetFitStatusForConfirmedFits,
  isConfirmedNoFit,
  setSolutionProductListItemImage,
} from '../../FitAssistantShared/Functions';
import { setFitStatus, setShowFitAssistant, setShowResetButtons, setStepEnum } from '../Slices/FitAssistantSlice.ts';
import { addMatchingProducts } from '../Slices/MatchingProductsSlice.ts';
import { Constants } from '../Constants.ts';
import { IsProductPage } from '../../FitAssistantShared/IsProductPage.js';
import { SelectedCarFromCookie } from '../Thunks/Types.ts';
import { trackFit } from '../Analytics/Adobe.ts';
import { ProductPageHelper } from './ProductPageHelper.ts';
import { MatchingProduct, SolutionPriceContainer } from '../Types/MatchingProducts.ts';
import { resetPricePLP, setPricePLP } from '../Util/ProductListUtils.ts';
import {
  handleDocuments,
  setAddToCartBtnForDBG,
  setImageBasedOnRoofRack,
  UpdateFitAssistantPriceAndATCForPDP,
  UpdateFitAssistantPriceAndATCForPDPExtranet,
} from '../Util/ProductPageUtils';
import FitAssistantStatus from '../Enums/FitAssistantStatus.ts';
import FitAssistantStep from '../Enums/FitAssistantStep.ts';
import FitAssistantVariation from '../Enums/FitAssistantVariation.ts';
import { CookieHelper } from './CookieHelper.ts';
import { InstructionTextHelper } from './InstructionTextHelper.ts';
import {
  getSelectedNonRoofRackStepData,
  getTentWeightForMatchingProducts,
  rooftopTentHasEligibleRacks,
} from './RooftopTentWeightHelper.ts';
import { getComponentsForSelectedSolution } from '../Util/SelectedSolutionComponents.ts';
import { updateSolutionBuyArea } from '../Util/SolutionBuyArea.ts';
import { getPerCategorySearchString, getSearchString } from '../Util/SearchString.ts';
import { setQueryFromSearchParameters } from '../Util/QueryFromParameters.ts';
import { onFitAssistantSaveData } from '../Analytics/Google.ts';

export class ProductLoader {
  data: GetProductsQuery;
  selectedCar: CarInformation[];
  selectedCarFromCookie: SelectedCarFromCookie | null;
  setCookie: boolean;
  initialData: InitialData | null;
  stepData: StepData[];
  shouldTriggerTracking: boolean;
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>;
  response: FitAssistantProductsResponse;

  constructor(
    data: GetProductsQuery,
    selectedCar: CarInformation[],
    selectedCarFromCookie: SelectedCarFromCookie | null,
    setCookie: boolean,
    initialData: InitialData | null = null,
    stepData: StepData[],
    shouldTriggerTracking: boolean,
    dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
  ) {
    this.data = data;
    this.selectedCar = selectedCar;
    this.selectedCarFromCookie = selectedCarFromCookie;
    this.setCookie = setCookie;
    this.initialData = initialData;
    this.stepData = stepData;
    this.shouldTriggerTracking = shouldTriggerTracking;
    this.dispatch = dispatch;

    this.response = data.getCarSelectorProducts.carSelectorProductsResponse;
  }

  public Load(): void {
    if (this.data && this.response) {
      if (this.initialData!.FitAssistantVariation === FitAssistantVariation.RoofPlatforms) {
        this.HandlePlatform();
      } else if (this.initialData!.FitAssistantVariation === FitAssistantVariation.RooftopTents) {
        this.HandleRooftopTents();
      } else if (this.initialData!.FitAssistantVariation === FitAssistantVariation.CarSeats) {
        this.HandleCarSeats();
      } else {
        this.HandleDefault();
      }
    }
  }

  private trackConfirmedCloseIfEnabled(): void {
    if (!this.shouldTriggerTracking) return;

    trackFit(
      this.initialData!.FitAssistantVariation,
      this.stepData.at(-1)?.parameters as FitAssistantStep,
      this.initialData!.IsYearFirst,
      this.selectedCar
    );
  }

  public HandleSelectListClick(): void {
    const fitStatus: FitAssistantStatus = this.SetCompatibility();

    this.dispatch(addMatchingProducts(this.data));
    this.dispatch(setFitStatus(fitStatus));

    if (fitStatus === FitAssistantStatus.NoFit) {
      if (this.response.MatchingProducts?.length > 0) {
        if (IsProductPage) {
          this.dispatch(setStepEnum(FitAssistantStep.OtherFits));
        } else {
          this.dispatch(setStepEnum(FitAssistantStep.NoFits));
          this.dispatch(setShowResetButtons(true));
        }
      } else {
        this.dispatch(setStepEnum(FitAssistantStep.NoFits));
        this.dispatch(setShowResetButtons(true));
      }
    }

    if (fitStatus === FitAssistantStatus.Unconfirmed) {
      this.dispatch(setStepEnum(FitAssistantStep.Unconfirmed));
    }

    if (fitStatus === FitAssistantStatus.Confirmed) {
      if (this.initialData!.FitAssistantVariation === FitAssistantVariation.CarSeats) {
        this.dispatch(setStepEnum(FitAssistantStep.CarSeatFit));
      } else {
        this.dispatch(setShowFitAssistant(false));
        this.dispatch(setShowResetButtons(true));

        this.trackConfirmedCloseIfEnabled();
      }
    }
  }

  private HandlePlatform(): void {
    let platformProducts = this.response.MatchingProducts?.filter(
      (x) => x.ObjectTypeId === Constants.SolutionTypes.PLATFORM
    );

    if (platformProducts?.length > 0) {
      const fitStatus: FitAssistantStatus = this.SetCompatibility();

      this.dispatch(addMatchingProducts(this.data));

      if (fitStatus === FitAssistantStatus.Confirmed) {
        let stepsNotIncludingRoofRack = this.selectedCar.filter(
          (car) => car.parameterName !== FitAssistantStep.RoofRacks
        );
        let stepToShow = stepsNotIncludingRoofRack[stepsNotIncludingRoofRack.length - 1].parameterName;
        this.dispatch(setStepEnum(stepToShow));
        this.dispatch(setShowFitAssistant(false));

        this.trackConfirmedCloseIfEnabled();
      } else if (
        this.response.MatchingProducts?.length > 0 &&
        this.response.MatchingProducts?.filter((x) => x.CompatibleBarForCaprock)?.length > 0
      ) {
        this.dispatch(setStepEnum(FitAssistantStep.RoofRacks));
      } else {
        this.dispatch(setStepEnum(FitAssistantStep.NoFits));
      }

      this.dispatch(setFitStatus(fitStatus));
      this.dispatch(setShowResetButtons(true));
    } else if (
      this.response.MatchingProducts?.length > 0 &&
      this.response.MatchingProducts?.filter((x) => x.CompatibleBarForCaprock)?.length > 0
    ) {
      if (this.selectedCarFromCookie?.getSliderList[4].data.find((x) => x.Selected)) {
        SetFitStatusForAllProducts(FitAssistantStatus.Confirmed);
        this.dispatch(setFitStatus(FitAssistantStatus.Confirmed));
        this.dispatch(setShowResetButtons(true));

        if (IsProductPage) {
          let currentStepData = this.stepData.find((x) => x.parameters === FitAssistantStep.RoofRacks);
          ProductPageHelper.HandleProductPageData(
            currentStepData,
            window._THULEDATA.ProductDetail,
            this.initialData!,
            this.selectedCar,
            this.dispatch
          );
        }
      } else {
        this.dispatch(setShowResetButtons(false));
      }

      this.dispatch(setStepEnum(FitAssistantStep.RoofRacks));
    } else {
      const fitStatus: FitAssistantStatus = this.SetCompatibility();

      this.dispatch(setFitStatus(fitStatus));
      this.dispatch(setStepEnum(FitAssistantStep.NoFits));
    }
  }

  private HandleRooftopTents(): void {
    const fitStatus: FitAssistantStatus = this.SetCompatibility();

    this.dispatch(addMatchingProducts(this.data));
    this.dispatch(setFitStatus(fitStatus));

    if (fitStatus === FitAssistantStatus.NoFit) this.dispatch(setStepEnum(FitAssistantStep.NoFits));

    if (fitStatus === FitAssistantStatus.Confirmed || fitStatus === FitAssistantStatus.ConfirmedNoRacks) {
      const selectedVariation = getSelectedNonRoofRackStepData(this.stepData);
      const tentWeight = this.GetRooftopTentWeightFromMatchingProducts();
      const showRacksStep = rooftopTentHasEligibleRacks(selectedVariation, tentWeight);
      this.dispatch(setStepEnum(showRacksStep ? FitAssistantStep.Confirmed : FitAssistantStep.ConfirmedNoRacks));
    }

    this.dispatch(setShowResetButtons(true));
  }

  private HandleCarSeats(): void {
    const fitStatus: FitAssistantStatus = this.SetCompatibility();

    this.dispatch(addMatchingProducts(this.data));
    this.dispatch(setFitStatus(fitStatus));

    if (fitStatus === FitAssistantStatus.NoFit) {
      if (this.response.MatchingProducts?.length > 0) {
        if (IsProductPage) {
          this.dispatch(setStepEnum(FitAssistantStep.OtherFits));
        } else {
          this.dispatch(setStepEnum(FitAssistantStep.NoFits));
        }
      } else {
        this.dispatch(setStepEnum(FitAssistantStep.NoFits));
      }
    }

    if (fitStatus === FitAssistantStatus.Unconfirmed) {
      this.dispatch(setStepEnum(FitAssistantStep.Unconfirmed));
    }

    if (fitStatus === FitAssistantStatus.Confirmed) {
      this.dispatch(setStepEnum(FitAssistantStep.CarSeatFit));
      this.dispatch(setShowFitAssistant(false));

      this.trackConfirmedCloseIfEnabled();
    }

    this.dispatch(setShowResetButtons(true));
  }

  private HandleDefault(): void {
    const fitStatus: FitAssistantStatus = this.SetCompatibility();

    this.dispatch(addMatchingProducts(this.data));
    this.dispatch(setFitStatus(fitStatus));

    if (fitStatus === FitAssistantStatus.NoFit) {
      if (this.response.MatchingProducts?.length > 0) {
        if (IsProductPage) {
          this.dispatch(setStepEnum(FitAssistantStep.OtherFits));
        } else {
          this.dispatch(setStepEnum(FitAssistantStep.NoFits));
        }
      } else {
        this.dispatch(setStepEnum(FitAssistantStep.NoFits));
      }
    }

    if (fitStatus === FitAssistantStatus.Unconfirmed) {
      this.dispatch(setStepEnum(FitAssistantStep.Unconfirmed));
    }

    if (fitStatus === FitAssistantStatus.Confirmed) {
      let stepsWithValue = this.selectedCar.filter((x) => x.value !== '0');
      let stepToShow = stepsWithValue[stepsWithValue.length - 1].parameterName;

      this.dispatch(setStepEnum(stepToShow));
      this.dispatch(setShowFitAssistant(false));

      // Hide the instruction translation on the product page since we have a car selected
      if (InstructionTextHelper.HasInstructionText(this.initialData!.FitAssistantVariation)) {
        InstructionTextHelper.HideInstructionText();
      }

      this.trackConfirmedCloseIfEnabled();
    }

    this.dispatch(setShowResetButtons(true));
  }

  private SetCompatibility(): FitAssistantStatus {
    if (!this.response || !this.response.Success) return FitAssistantStatus.Unconfirmed;

    let confirmedCards: HTMLDivElement[] = [];
    let fitStatus = FitAssistantStatus.NoFit;

    if (this.response.SolutionComponents) {
      handleDocuments(this.response.SolutionComponents);
    }

    if (this.setCookie) {
      const selection = getSearchString(this.selectedCar);
      const perCategorySelection = getPerCategorySearchString(this.selectedCar);
      CookieHelper.SetCookies(selection, perCategorySelection);

      onFitAssistantSaveData(this.selectedCar);

      if (IsProductPage) setQueryFromSearchParameters(this.initialData!.FitAssistantCookieName, this.selectedCar);
    }

    if (IsProductPage) {
      fitStatus =
        this.initialData!.FitAssistantVariation === FitAssistantVariation.RooftopTents
          ? this.SetRTTCompatibilityProductPage()
          : this.setCompatibilityForProductPage();
    } else {
      fitStatus =
        this.initialData!.FitAssistantVariation === FitAssistantVariation.RooftopTents
          ? this.SetRTTCompatibilityProductListing(confirmedCards)
          : this.setCompatibilityForProductProductListing(confirmedCards);
    }

    return fitStatus;
  }

  private setCompatibilityForProductPage(): FitAssistantStatus {
    let solutionIdsElement: HTMLInputElement = document.querySelector('.product-ids-for-solution') as HTMLInputElement;
    let productIdElement: HTMLInputElement = document.querySelector('.product-id') as HTMLInputElement;
    let buyAreaElement: HTMLDivElement = document.querySelector('#productBuyArea') as HTMLDivElement;

    let productId = productIdElement?.value ?? buyAreaElement.dataset.productid;

    let fitStatus = this.response.ProductPageMatch
      ? FitAssistantStatus.Confirmed
      : isConfirmedNoFit(this.response.NoFitIds, productId)
        ? FitAssistantStatus.NoFit
        : this.initialData!.DisableUnconfirmed
          ? FitAssistantStatus.NoFit
          : FitAssistantStatus.Unconfirmed;

    if (solutionIdsElement) {
      let bundleIds = solutionIdsElement.dataset.defaultids;

      for (let product of this.response.MatchingProducts) {
        if (bundleIds?.includes(product.PimId)) {
          fitStatus = FitAssistantStatus.Confirmed;
          break;
        }
      }
    }

    if (window._THULEDATA?.isExtranet) {
      const components = getComponentsForSelectedSolution(
        this.response.SolutionComponents,
        this.response.MatchingProducts,
        0
      );
      UpdateFitAssistantPriceAndATCForPDPExtranet(components);
    } else {
      UpdateFitAssistantPriceAndATCForPDP(this.response.SolutionPriceContainer[0], this.response.AddToCartButton[0]);
    }

    setImageBasedOnRoofRack(this.response);

    return fitStatus;
  }

  private setCompatibilityForProductProductListing(confirmedCards: HTMLDivElement[]): FitAssistantStatus {
    let matchingPimIds = this.response.MatchingProducts.map((x) => {
      return x.PimId;
    });
    const priceByPimId = this.getSolutionPricesByPimId();

    let allPimIds: string[] = [];
    let cards: NodeListOf<HTMLDivElement> = document.querySelectorAll('div.isotope-item[data-pimid]');
    cards.forEach((card) => {
      let pimId: string = card.getAttribute('data-pimid') as string;
      let matchingProduct: MatchingProduct | undefined;

      if (pimId) allPimIds.push(pimId);

      const gpc = (card as any).__groupedProductCard;
      const gpcPricePainted = gpc ? this.setSolutionPricesForGroupedCard(gpc, priceByPimId) : false;
      if (!gpc && !(pimId && priceByPimId[pimId])) resetPricePLP(card);

      // Grouped cards carry the leader pimid on the root, but a confirmed fit
      // can sit on a non-leader variant — match against all variants.
      const matchedPimId =
        pimId && matchingPimIds.indexOf(pimId) > -1
          ? pimId
          : gpc?.variants?.find((v: { PimId: string }) => matchingPimIds.includes(v.PimId))?.PimId;

      if (matchedPimId) {
        confirmedCards.push(card);
        matchingProduct =
          this.response.MatchingProducts.find((x) => x.PimId === matchedPimId && x.IsDefaultSolution) ??
          this.response.MatchingProducts.find((x) => x.PimId === matchedPimId);

        if (!gpcPricePainted) setPricePLP(card, matchingProduct?.SolutionPriceContainer);
        setAddToCartBtnForDBG(card, matchingProduct);

        if (
          !IsProductPage &&
          window._THULEDATA.FitAssistant.FitAssistantVariation == FitAssistantVariation.CarRoofRacks
        )
          setSolutionProductListItemImage(card, matchingProduct);
      } else if (
        !IsProductPage &&
        window._THULEDATA.FitAssistant.FitAssistantVariation == FitAssistantVariation.CarSeats
      ) {
        const bundleIds: string = card.getAttribute('data-bundle-ids') as string;
        if (bundleIds) {
          let bundleIdArray: string[] = bundleIds.split(',');
          let intersection: string[] = bundleIdArray.filter((bundle) => matchingPimIds.includes(bundle));

          if (intersection.length > 0) confirmedCards.push(card);
        }

        setSolutionProductListItemImage(card, matchingProduct);
      }

      updateSolutionBuyArea(card, matchingProduct);
    });

    SetFitStatusForConfirmedFits(confirmedCards, this.response?.NoFitIds, this.initialData, matchingPimIds);
    if (confirmedCards.length > 0) {
      return FitAssistantStatus.Confirmed;
    } else if (
      confirmedCards.length === 0 &&
      (this.response.NoFitIds.length === 0 || !allPimIds.every((x) => this.response.NoFitIds.includes(x))) &&
      !this.initialData!.DisableUnconfirmed
    ) {
      return FitAssistantStatus.Unconfirmed;
    } else {
      return FitAssistantStatus.NoFit;
    }
  }

  private getSolutionPricesByPimId(): Record<string, SolutionPriceContainer> {
    const priceByPimId: Record<string, SolutionPriceContainer> = {};
    this.response.MatchingProducts.forEach((x) => {
      if (x.SolutionPriceContainer) priceByPimId[x.PimId] = x.SolutionPriceContainer;
    });
    return priceByPimId;
  }

  private setSolutionPricesForGroupedCard(gpc: any, priceByPimId: Record<string, SolutionPriceContainer>): boolean {
    const familyPrices: Record<string, SolutionPriceContainer> = {};
    gpc.variants?.forEach((variant: { PimId: string }) => {
      if (priceByPimId[variant.PimId]) familyPrices[variant.PimId] = priceByPimId[variant.PimId];
    });
    return gpc.setSolutionPrices(familyPrices) === true;
  }

  private SetRTTCompatibilityProductPage(): FitAssistantStatus {
    if (!this.response || !this.response.Success) return FitAssistantStatus.NoFit;

    let solutionIdsElement: HTMLInputElement = document.querySelector('.product-ids-for-solution') as HTMLInputElement;

    const selectedVariation = getSelectedNonRoofRackStepData(this.stepData);
    const tentWeight = this.GetRooftopTentWeightFromMatchingProducts();
    let fitStatus = this.response.ProductPageMatch
      ? rooftopTentHasEligibleRacks(selectedVariation, tentWeight)
        ? FitAssistantStatus.Confirmed
        : FitAssistantStatus.ConfirmedNoRacks
      : FitAssistantStatus.NoFit;

    if (solutionIdsElement) {
      let bundleIds = solutionIdsElement.dataset.defaultids;

      for (let product of this.response.MatchingProducts) {
        if (bundleIds?.includes(product.PimId)) {
          const w = product.TentWeight ?? 0;
          fitStatus = rooftopTentHasEligibleRacks(selectedVariation, w)
            ? FitAssistantStatus.Confirmed
            : FitAssistantStatus.ConfirmedNoRacks;
          break;
        }
      }
    }

    return fitStatus;
  }

  private SetRTTCompatibilityProductListing(confirmedCards: HTMLDivElement[]): FitAssistantStatus {
    let matchingPimIds = this.response.MatchingProducts.map((x) => {
      return x.PimId;
    });
    const priceByPimId = this.getSolutionPricesByPimId();

    let allPimIds: string[] = [];
    let anyMatchingPimOnPage = false;
    let cards: NodeListOf<HTMLDivElement> = document.querySelectorAll('div.isotope-item[data-pimid]');
    cards.forEach((card) => {
      let pimId: string = card.getAttribute('data-pimid') as string;
      let matchingProduct: MatchingProduct | undefined;

      if (pimId) allPimIds.push(pimId);

      const gpc = (card as any).__groupedProductCard;
      const gpcPricePainted = gpc ? this.setSolutionPricesForGroupedCard(gpc, priceByPimId) : false;
      if (!gpc && !(pimId && priceByPimId[pimId])) resetPricePLP(card);

      const matchedPimId =
        pimId && matchingPimIds.indexOf(pimId) > -1
          ? pimId
          : gpc?.variants?.find((v: { PimId: string }) => matchingPimIds.includes(v.PimId))?.PimId;

      if (matchedPimId) {
        anyMatchingPimOnPage = true;
        matchingProduct = this.response.MatchingProducts.find((x) => x.PimId === matchedPimId);
        confirmedCards.push(card);

        if (!gpcPricePainted) setPricePLP(card, matchingProduct?.SolutionPriceContainer);
        setAddToCartBtnForDBG(card, matchingProduct);
      }
    });

    SetFitStatusForConfirmedFits(confirmedCards, this.response?.NoFitIds, this.initialData, matchingPimIds);
    if (confirmedCards.length > 0) {
      return FitAssistantStatus.Confirmed;
    }
    if (anyMatchingPimOnPage) {
      return FitAssistantStatus.ConfirmedNoRacks;
    }
    return FitAssistantStatus.NoFit;
  }

  private GetRooftopTentWeightFromMatchingProducts(): number {
    const products = this.response?.MatchingProducts;
    if (!products?.length) return 0;

    const productId = IsProductPage ? window._THULEDATA?.ProductDetail?.ProductId : undefined;
    return getTentWeightForMatchingProducts(products, productId);
  }
}
